import { Component } from '@theme/component';
import { normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement} [infiniteScrollLoader] - Shown while the next page is loading.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((e: Event) => void) | null} */
  #pendingFilterMorphHandler = null;

  /** @type {ReturnType<typeof setTimeout> | 0} */
  #filterMorphFallbackTimeout = 0;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {Promise<void> | null} */
  #inflightNextPage = null;

  /** @type {boolean} */
  #renderingNextPage = false;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    // Next page loads when the bottom sentinel intersects, so the loader is on screen while products load.
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    this.#clearPendingFilterMorphHandler();
    super.disconnectedCallback();
    this.#setInfiniteScrollLoading(false);
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #clearPendingFilterMorphHandler() {
    if (this.#pendingFilterMorphHandler) {
      document.removeEventListener('shopify:section:morphed', this.#pendingFilterMorphHandler);
      this.#pendingFilterMorphHandler = null;
    }
    if (this.#filterMorphFallbackTimeout) {
      clearTimeout(this.#filterMorphFallbackTimeout);
      this.#filterMorphFallbackTimeout = 0;
    }
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '0px 0px 240px 0px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      viewMoreNext.removeAttribute('hidden');
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    if (type === 'next' && this.#inflightNextPage) return this.#inflightNextPage;

    const request = this.#fetchPageUncached(type);
    if (type !== 'next') return request;

    this.#inflightNextPage = request.finally(() => {
      this.#inflightNextPage = null;
    });
    return this.#inflightNextPage;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPageUncached(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    if (this.pages.has(page.page)) {
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } finally {
      resolvePromise();
    }
  }

  #loaderElement() {
    return this.querySelector('.plp-async-products-loader') || this.refs.infiniteScrollLoader || null;
  }

  /** @param {boolean} visible */
  #setInfiniteScrollLoading(visible) {
    const host = this.#loaderElement();
    if (!(host instanceof HTMLElement)) return;

    if (visible) {
      host.hidden = false;
      host.setAttribute('aria-busy', 'true');
      return;
    }

    host.hidden = true;
    host.removeAttribute('aria-busy');
  }

  #revealInfiniteScrollLoader() {
    const host = this.#loaderElement();
    if (!(host instanceof HTMLElement) || host.hidden) return;

    const top = host.getBoundingClientRect().top;
    if (top > window.innerHeight - 48) {
      host.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    }
  }

  #handleReachedEndOfNextPages() {
    this.#clearCardSkeletons();
    const { viewMoreNext } = this.refs;
    if (viewMoreNext instanceof HTMLElement) {
      viewMoreNext.hidden = true;
      this.infinityScrollObserver?.unobserve(viewMoreNext);
    }
  }

  /**
   * @param {HTMLElement} grid
   */
  #skeletonCount(grid) {
    const columns = getComputedStyle(grid)
      .gridTemplateColumns.split(' ')
      .filter((part) => part && part !== 'none').length;
    return Math.min(Math.max(columns || 2, 2) * 2, 8);
  }

  #showCardSkeletons() {
    const { grid } = this.refs;
    if (!grid || grid.querySelector(':scope > .plp-card-skeleton')) return;

    const template = document.createElement('li');
    template.className = 'product-grid__item plp-card-skeleton';
    template.setAttribute('aria-hidden', 'true');

    const media = document.createElement('div');
    media.className = 'plp-card-skeleton__media';
    const copy = document.createElement('div');
    copy.className = 'plp-card-skeleton__copy';
    const line = document.createElement('span');
    line.className = 'plp-card-skeleton__line';
    const shortLine = /** @type {HTMLElement} */ (line.cloneNode());
    shortLine.classList.add('plp-card-skeleton__line--short');
    copy.append(line, shortLine);
    template.append(media, copy);

    const frag = document.createDocumentFragment();
    const count = this.#skeletonCount(grid);
    for (let index = 0; index < count; index += 1) {
      frag.appendChild(template.cloneNode(true));
    }
    grid.appendChild(frag);
  }

  #clearCardSkeletons() {
    const { grid } = this.refs;
    if (!grid) return;
    grid.querySelectorAll(':scope > .plp-card-skeleton').forEach((node) => node.remove());
  }

  /**
   * @param {NodeListOf<Element> | Element[]} nodes
   */
  #markIncomingCardMedia(nodes) {
    for (const node of nodes) {
      if (!(node instanceof HTMLElement) || !node.classList.contains('product-grid__item')) continue;
      const img = node.querySelector('img');
      if (!(img instanceof HTMLImageElement) || (img.complete && img.naturalWidth > 0)) continue;

      node.classList.add('plp-card-media-loading');
      const done = () => node.classList.remove('plp-card-media-loading');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    if (this.#renderingNextPage) return;

    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) {
      this.#handleReachedEndOfNextPages();
      return;
    }

    this.#renderingNextPage = true;
    const shownAt = performance.now();
    this.#setInfiniteScrollLoading(true);

    try {
      await yieldToMainThread();
      let nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) {
        this.#showCardSkeletons();
        await yieldToMainThread();
        this.#revealInfiniteScrollLoader();
        await this.#fetchPage('next');
        await yieldToMainThread();
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        this.#clearCardSkeletons();
        if (!nextPageItemElements) {
          this.#handleReachedEndOfNextPages();
          return;
        }
      }

      this.#markIncomingCardMedia(nextPageItemElements);
      grid.append(...nextPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      history.pushState('', '', nextPage.url.toString());
    } finally {
      this.#clearCardSkeletons();
      const remaining = 450 - (performance.now() - shownAt);
      if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));
      this.#setInfiniteScrollLoading(false);
      this.#renderingNextPage = false;
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"], :scope > .main-collection-ingrid-strip');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    this.#clearPendingFilterMorphHandler();

    /**
     * After facet filtering, the section is morphed and `[ref="grid"]` is often replaced.
     * A MutationObserver on the *old* grid never sees updates, so infinite scroll never re-binds.
     * `shopify:section:morphed` fires when SFR morph completes (see section-renderer.js).
     */
    const mySectionId = normalizeSectionId(this.sectionId);
    let finalized = false;
    const finalizeAfterFilterMorph = () => {
      if (finalized) return;
      finalized = true;
      this.#clearPendingFilterMorphHandler();
      if (!this.isConnected) return;
      this.#observeViewMore();
    };

    this.#pendingFilterMorphHandler = (e) => {
      const morphedId = e?.detail?.sectionId;
      if (!morphedId || normalizeSectionId(morphedId) !== mySectionId) return;
      finalizeAfterFilterMorph();
    };

    document.addEventListener('shopify:section:morphed', this.#pendingFilterMorphHandler);

    this.#filterMorphFallbackTimeout = setTimeout(() => finalizeAfterFilterMorph(), 3000);
  };
}
